(() => {
  const builders = document.querySelectorAll('[data-pc-builder]');

  const normalizeCode = (value) => {
    const normalized = String(value || '').trim();
    if (!normalized || normalized.toLowerCase() === 'none') return '';
    return normalized.toUpperCase().replace(/\s+/g, '');
  };

  const splitCodes = (value) => String(value || '')
    .split(/[,、|]/)
    .map(normalizeCode)
    .filter(Boolean);

  const toNumber = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  };

  const includesCode = (list, value) => {
    const target = normalizeCode(value);
    return Boolean(target) && splitCodes(list).includes(target);
  };

  builders.forEach((builder) => {
    const mode = builder.dataset.mode || 'custom';
    const currency = builder.dataset.currency || 'JPY';
    const redirectAfterAdd = builder.dataset.redirect || 'cart';
    const compatibilityEnabled = builder.dataset.compatibilityEnabled === 'true';
    const strictCompatibility = builder.dataset.strictCompatibility === 'true';
    const addButton = builder.querySelector('[data-add-pc]');
    const summaryList = builder.querySelector('[data-pc-summary-list]');
    const totalElement = builder.querySelector('[data-pc-total]');
    const errorElement = builder.querySelector('[data-pc-error]');
    const compatibilityPanel = builder.querySelector('[data-pc-compatibility]');
    const compatibilityBadge = builder.querySelector('[data-compatibility-badge]');
    const compatibilityList = builder.querySelector('[data-compatibility-list]');
    const groups = [...builder.querySelectorAll('[data-option-group]')];
    const partInputs = [...builder.querySelectorAll('input[type="radio"][data-variant-id]')];
    const baseGroup = builder.querySelector('[data-group="base"]');
    const btoPartGroups = [...builder.querySelectorAll('[data-bto-part-group]')];

    const money = new Intl.NumberFormat(
      document.documentElement.lang || 'ja-JP',
      {
        style: 'currency',
        currency,
        maximumFractionDigits: currency === 'JPY' ? 0 : 2
      }
    );

    const formatMoney = (cents) => money.format(Number(cents || 0) / 100);

    function getItemFromInput(input) {
      if (!input || !input.dataset.variantId) return null;

      return {
        input,
        variantId: Number(input.dataset.variantId),
        price: Number(input.dataset.price || 0),
        categoryKey: input.dataset.categoryKey || '',
        category: input.dataset.categoryLabel || '選択項目',
        label: input.dataset.label || '商品',
        socket: normalizeCode(input.dataset.socket),
        supportedSockets: input.dataset.supportedSockets || '',
        cpuCode: normalizeCode(input.dataset.cpuCode),
        supportedCpuCodes: input.dataset.supportedCpuCodes || '',
        memoryStandard: normalizeCode(input.dataset.memoryStandard),
        mbFormFactor: normalizeCode(input.dataset.mbFormFactor),
        caseMbFormFactors: input.dataset.caseMbFormFactors || '',
        storageInterface: normalizeCode(input.dataset.storageInterface),
        mbStorageInterfaces: input.dataset.mbStorageInterfaces || '',
        memoryCapacityGb: toNumber(input.dataset.memoryCapacityGb),
        memoryModuleCount: toNumber(input.dataset.memoryModuleCount),
        mbMaxMemoryGb: toNumber(input.dataset.mbMaxMemoryGb),
        mbDimmSlots: toNumber(input.dataset.mbDimmSlots),
        gpuLengthMm: toNumber(input.dataset.gpuLengthMm),
        gpuSlots: toNumber(input.dataset.gpuSlots),
        gpuRequiredPsuW: toNumber(input.dataset.gpuRequiredPsuW),
        caseMaxGpuLengthMm: toNumber(input.dataset.caseMaxGpuLengthMm),
        caseMaxGpuSlots: toNumber(input.dataset.caseMaxGpuSlots),
        coolerType: normalizeCode(input.dataset.coolerType),
        coolerHeightMm: toNumber(input.dataset.coolerHeightMm),
        radiatorSizeMm: toNumber(input.dataset.radiatorSizeMm),
        coolerCapacityW: toNumber(input.dataset.coolerCapacityW),
        cpuCoolingRequiredW: toNumber(input.dataset.cpuCoolingRequiredW),
        caseMaxCoolerHeightMm: toNumber(input.dataset.caseMaxCoolerHeightMm),
        caseRadiatorSizes: input.dataset.caseRadiatorSizes || '',
        psuWattage: toNumber(input.dataset.psuWattage),
        psuFormFactor: normalizeCode(input.dataset.psuFormFactor),
        casePsuFormFactors: input.dataset.casePsuFormFactors || '',
        gpuPowerConnectors: input.dataset.gpuPowerConnectors || '',
        psuGpuConnectors: input.dataset.psuGpuConnectors || '',
        modelCode: normalizeCode(input.dataset.modelCode),
        requiredCategories: input.dataset.requiredCategories || ''
      };
    }

    function getSelectedItems() {
      return groups
        .filter((group) => !group.hidden)
        .map((group) => {
          const input = group.querySelector('input[type="radio"]:checked');
          if (!input || input.closest('.pc-option-card')?.hidden) return null;
          return getItemFromInput(input);
        })
        .filter(Boolean);
    }

    function getSelectedMap(items = getSelectedItems()) {
      return items.reduce((map, item) => {
        map[item.categoryKey] = item;
        return map;
      }, {});
    }

    function getSelectedBaseInput() {
      return baseGroup?.querySelector('input[type="radio"][data-variant-id]:checked') || null;
    }

    function isOptionAvailableForModel(input, modelCode) {
      if (!modelCode) return false;
      if (input.dataset.btoAllModels === 'true') return true;
      return includesCode(input.dataset.btoModelCodes, modelCode);
    }

    function isDefaultForModel(input, modelCode) {
      return includesCode(input.dataset.btoDefaultModelCodes, modelCode);
    }

    function applyBtoModelConfiguration({ resetSelections = false } = {}) {
      if (mode !== 'bto') return;

      const baseInput = getSelectedBaseInput();
      const modelCode = normalizeCode(baseInput?.dataset.modelCode);
      const requiredCategories = baseInput?.dataset.requiredCategories || '';

      btoPartGroups.forEach((group) => {
        const categoryKey = group.dataset.group || '';
        const required = Boolean(modelCode) && includesCode(requiredCategories, categoryKey);
        const requiredBadge = group.querySelector('[data-required-badge]');
        const emptyCard = group.querySelector('[data-empty-option]');
        const emptyInput = emptyCard?.querySelector('input[type="radio"]');
        const noOptionsMessage = group.querySelector('[data-bto-no-options]');
        const cards = [...group.querySelectorAll('[data-bto-option-card]')];
        const visibleInputs = [];

        cards.forEach((card) => {
          const input = card.querySelector('input[type="radio"][data-variant-id]');
          if (!input) return;

          const allowed = isOptionAvailableForModel(input, modelCode);
          card.hidden = !allowed;
          input.dataset.modelHidden = allowed ? 'false' : 'true';

          if (!allowed) {
            input.checked = false;
            input.disabled = true;
            return;
          }

          input.disabled = input.dataset.stockDisabled === 'true';
          visibleInputs.push(input);
        });

        const hasVisibleOptions = visibleInputs.length > 0;
        group.dataset.required = required ? 'true' : 'false';
        group.hidden = !modelCode || (!hasVisibleOptions && !required);
        group.classList.toggle('pc-option-group--model-empty', required && !hasVisibleOptions);

        if (requiredBadge) requiredBadge.hidden = !required;
        if (noOptionsMessage) noOptionsMessage.hidden = !(required && !hasVisibleOptions);

        if (emptyCard) emptyCard.hidden = required;
        if (emptyInput) emptyInput.disabled = required || group.hidden;

        if (group.hidden) {
          visibleInputs.forEach((input) => { input.checked = false; });
          if (emptyInput) emptyInput.checked = false;
          return;
        }

        const selectedVisible = visibleInputs.find((input) => input.checked && !input.disabled);
        if (!resetSelections && selectedVisible) return;

        visibleInputs.forEach((input) => { input.checked = false; });
        if (emptyInput) emptyInput.checked = false;

        const defaultInput = visibleInputs.find((input) =>
          !input.disabled && isDefaultForModel(input, modelCode)
        );

        if (defaultInput) {
          defaultInput.checked = true;
        } else if (!required && emptyInput) {
          emptyInput.checked = true;
        }
      });
    }

    function makeIssue(type, code, message, categories = []) {
      return { type, code, message, categories };
    }

    function metadataIssue(item, fieldLabel) {
      return makeIssue(
        strictCompatibility ? 'error' : 'warning',
        `metadata-${item.categoryKey}-${fieldLabel}`,
        `${item.label}：${fieldLabel}が未入力です。`,
        [item.categoryKey]
      );
    }

    function getMetadataIssues(items) {
      const issues = [];

      items.forEach((item) => {
        switch (item.categoryKey) {
          case 'cpu':
            if (!item.socket) issues.push(metadataIssue(item, 'ソケット'));
            break;
          case 'cpu_cooler':
            if (splitCodes(item.supportedSockets).length === 0) issues.push(metadataIssue(item, '対応ソケット'));
            if (!item.coolerType) issues.push(metadataIssue(item, 'クーラー種類'));
            if (item.coolerType === 'AIR' && !item.coolerHeightMm) issues.push(metadataIssue(item, 'クーラー高さ'));
            if (item.coolerType === 'AIO' && !item.radiatorSizeMm) issues.push(metadataIssue(item, 'ラジエーターサイズ'));
            break;
          case 'motherboard':
            if (!item.socket) issues.push(metadataIssue(item, 'ソケット'));
            if (!item.memoryStandard) issues.push(metadataIssue(item, 'メモリ規格'));
            if (!item.mbFormFactor) issues.push(metadataIssue(item, 'マザーボード規格'));
            if (splitCodes(item.mbStorageInterfaces).length === 0) issues.push(metadataIssue(item, '対応ストレージ規格'));
            break;
          case 'memory':
            if (!item.memoryStandard) issues.push(metadataIssue(item, 'メモリ規格'));
            if (!item.memoryCapacityGb) issues.push(metadataIssue(item, 'メモリ容量'));
            if (!item.memoryModuleCount) issues.push(metadataIssue(item, 'メモリ枚数'));
            break;
          case 'gpu':
            if (!item.gpuLengthMm) issues.push(metadataIssue(item, 'GPU長'));
            if (!item.gpuRequiredPsuW) issues.push(metadataIssue(item, '推奨電源容量'));
            break;
          case 'storage':
            if (!item.storageInterface) issues.push(metadataIssue(item, 'ストレージ接続規格'));
            break;
          case 'case':
            if (splitCodes(item.caseMbFormFactors).length === 0) issues.push(metadataIssue(item, '対応マザーボード規格'));
            if (!item.caseMaxGpuLengthMm) issues.push(metadataIssue(item, '最大GPU長'));
            if (splitCodes(item.casePsuFormFactors).length === 0) issues.push(metadataIssue(item, '対応電源規格'));
            break;
          case 'psu':
            if (!item.psuWattage) issues.push(metadataIssue(item, '電源容量'));
            if (!item.psuFormFactor) issues.push(metadataIssue(item, '電源規格'));
            break;
          default:
            break;
        }
      });

      return issues;
    }

    function checkCompatibility(items, { includeMetadata = true } = {}) {
      if (!compatibilityEnabled) return [];

      const selected = getSelectedMap(items);
      const issues = includeMetadata ? getMetadataIssues(items) : [];
      const cpu = selected.cpu;
      const cooler = selected.cpu_cooler;
      const motherboard = selected.motherboard;
      const memory = selected.memory;
      const gpu = selected.gpu;
      const storage = selected.storage;
      const pcCase = selected.case;
      const psu = selected.psu;

      if (cpu && motherboard) {
        if (cpu.socket && motherboard.socket && cpu.socket !== motherboard.socket) {
          issues.push(makeIssue(
            'error',
            'cpu-motherboard-socket',
            `CPUソケット「${cpu.socket}」とマザーボード「${motherboard.socket}」が一致しません。`,
            ['cpu', 'motherboard']
          ));
        }

        if (
          cpu.cpuCode &&
          splitCodes(motherboard.supportedCpuCodes).length > 0 &&
          !includesCode(motherboard.supportedCpuCodes, cpu.cpuCode)
        ) {
          issues.push(makeIssue(
            'error',
            'cpu-generation',
            `選択したマザーボードはCPU世代コード「${cpu.cpuCode}」に対応していません。`,
            ['cpu', 'motherboard']
          ));
        }
      }

      if (cpu && cooler) {
        if (
          cpu.socket &&
          splitCodes(cooler.supportedSockets).length > 0 &&
          !includesCode(cooler.supportedSockets, cpu.socket)
        ) {
          issues.push(makeIssue(
            'error',
            'cpu-cooler-socket',
            `CPUクーラーがソケット「${cpu.socket}」に対応していません。`,
            ['cpu', 'cpu_cooler']
          ));
        }

        if (
          cpu.cpuCoolingRequiredW &&
          cooler.coolerCapacityW &&
          cooler.coolerCapacityW < cpu.cpuCoolingRequiredW
        ) {
          issues.push(makeIssue(
            'error',
            'cpu-cooler-capacity',
            `CPUの必要冷却目安 ${cpu.cpuCoolingRequiredW}W に対し、クーラーの冷却目安は ${cooler.coolerCapacityW}W です。`,
            ['cpu', 'cpu_cooler']
          ));
        }
      }

      if (motherboard && memory) {
        if (
          motherboard.memoryStandard &&
          memory.memoryStandard &&
          motherboard.memoryStandard !== memory.memoryStandard
        ) {
          issues.push(makeIssue(
            'error',
            'memory-standard',
            `マザーボードは ${motherboard.memoryStandard}、メモリは ${memory.memoryStandard} です。`,
            ['motherboard', 'memory']
          ));
        }

        if (
          motherboard.mbMaxMemoryGb &&
          memory.memoryCapacityGb &&
          memory.memoryCapacityGb > motherboard.mbMaxMemoryGb
        ) {
          issues.push(makeIssue(
            'error',
            'memory-capacity',
            `メモリ ${memory.memoryCapacityGb}GB はマザーボード上限 ${motherboard.mbMaxMemoryGb}GB を超えています。`,
            ['motherboard', 'memory']
          ));
        }

        if (
          motherboard.mbDimmSlots &&
          memory.memoryModuleCount &&
          memory.memoryModuleCount > motherboard.mbDimmSlots
        ) {
          issues.push(makeIssue(
            'error',
            'memory-slots',
            `メモリ ${memory.memoryModuleCount}枚に対し、マザーボードのDIMMスロットは ${motherboard.mbDimmSlots}基です。`,
            ['motherboard', 'memory']
          ));
        }
      }

      if (motherboard && storage) {
        if (
          storage.storageInterface &&
          splitCodes(motherboard.mbStorageInterfaces).length > 0 &&
          !includesCode(motherboard.mbStorageInterfaces, storage.storageInterface)
        ) {
          issues.push(makeIssue(
            'error',
            'storage-interface',
            `マザーボードがストレージ規格「${storage.storageInterface}」に対応していません。`,
            ['motherboard', 'storage']
          ));
        }
      }

      if (motherboard && pcCase) {
        if (
          motherboard.mbFormFactor &&
          splitCodes(pcCase.caseMbFormFactors).length > 0 &&
          !includesCode(pcCase.caseMbFormFactors, motherboard.mbFormFactor)
        ) {
          issues.push(makeIssue(
            'error',
            'case-motherboard-form-factor',
            `ケースがマザーボード規格「${motherboard.mbFormFactor}」に対応していません。`,
            ['motherboard', 'case']
          ));
        }
      }

      if (gpu && pcCase) {
        if (
          gpu.gpuLengthMm &&
          pcCase.caseMaxGpuLengthMm &&
          gpu.gpuLengthMm > pcCase.caseMaxGpuLengthMm
        ) {
          issues.push(makeIssue(
            'error',
            'case-gpu-length',
            `GPU長 ${gpu.gpuLengthMm}mm はケース上限 ${pcCase.caseMaxGpuLengthMm}mm を超えています。`,
            ['gpu', 'case']
          ));
        }

        if (
          gpu.gpuSlots &&
          pcCase.caseMaxGpuSlots &&
          gpu.gpuSlots > pcCase.caseMaxGpuSlots
        ) {
          issues.push(makeIssue(
            'error',
            'case-gpu-slots',
            `GPU占有 ${gpu.gpuSlots}スロットはケース上限 ${pcCase.caseMaxGpuSlots}スロットを超えています。`,
            ['gpu', 'case']
          ));
        }
      }

      if (cooler && pcCase) {
        if (
          cooler.coolerType === 'AIR' &&
          cooler.coolerHeightMm &&
          pcCase.caseMaxCoolerHeightMm &&
          cooler.coolerHeightMm > pcCase.caseMaxCoolerHeightMm
        ) {
          issues.push(makeIssue(
            'error',
            'case-air-cooler-height',
            `空冷クーラー高 ${cooler.coolerHeightMm}mm はケース上限 ${pcCase.caseMaxCoolerHeightMm}mm を超えています。`,
            ['cpu_cooler', 'case']
          ));
        }

        if (
          cooler.coolerType === 'AIO' &&
          cooler.radiatorSizeMm &&
          splitCodes(pcCase.caseRadiatorSizes).length > 0 &&
          !includesCode(pcCase.caseRadiatorSizes, String(cooler.radiatorSizeMm))
        ) {
          issues.push(makeIssue(
            'error',
            'case-radiator-size',
            `ケースが ${cooler.radiatorSizeMm}mm ラジエーターに対応していません。`,
            ['cpu_cooler', 'case']
          ));
        }

        if (
          cooler.coolerType === 'AIR' &&
          cooler.coolerHeightMm &&
          !pcCase.caseMaxCoolerHeightMm
        ) {
          issues.push(makeIssue(
            strictCompatibility ? 'error' : 'warning',
            'case-missing-cooler-height',
            `${pcCase.label}：最大空冷クーラー高さが未入力です。`,
            ['case', 'cpu_cooler']
          ));
        }

        if (
          cooler.coolerType === 'AIO' &&
          cooler.radiatorSizeMm &&
          splitCodes(pcCase.caseRadiatorSizes).length === 0
        ) {
          issues.push(makeIssue(
            strictCompatibility ? 'error' : 'warning',
            'case-missing-radiator-size',
            `${pcCase.label}：対応ラジエーターサイズが未入力です。`,
            ['case', 'cpu_cooler']
          ));
        }
      }

      if (psu && pcCase) {
        if (
          psu.psuFormFactor &&
          splitCodes(pcCase.casePsuFormFactors).length > 0 &&
          !includesCode(pcCase.casePsuFormFactors, psu.psuFormFactor)
        ) {
          issues.push(makeIssue(
            'error',
            'case-psu-form-factor',
            `ケースが電源規格「${psu.psuFormFactor}」に対応していません。`,
            ['psu', 'case']
          ));
        }
      }

      if (gpu && psu) {
        if (
          gpu.gpuRequiredPsuW &&
          psu.psuWattage &&
          psu.psuWattage < gpu.gpuRequiredPsuW
        ) {
          issues.push(makeIssue(
            'error',
            'gpu-psu-wattage',
            `GPU推奨 ${gpu.gpuRequiredPsuW}W に対し、選択電源は ${psu.psuWattage}W です。`,
            ['gpu', 'psu']
          ));
        }

        const requiredConnectors = splitCodes(gpu.gpuPowerConnectors);
        const supportedConnectors = splitCodes(psu.psuGpuConnectors);
        if (
          requiredConnectors.length > 0 &&
          supportedConnectors.length > 0 &&
          requiredConnectors.some((connector) => !supportedConnectors.includes(connector))
        ) {
          issues.push(makeIssue(
            'error',
            'gpu-psu-connectors',
            '電源のGPU補助電源コネクタが不足しています。',
            ['gpu', 'psu']
          ));
        }
      }

      const unique = new Map();
      issues.forEach((issue) => unique.set(issue.code, issue));
      return [...unique.values()];
    }

    function getMissingRequiredGroups() {
      return groups.filter((group) => {
        if (group.hidden || group.dataset.required !== 'true') return false;
        const selected = group.querySelector('input[type="radio"]:checked');
        return !selected || !selected.dataset.variantId;
      });
    }

    function validate() {
      const missing = getMissingRequiredGroups();
      if (missing.length > 0) {
        const labels = missing.map((group) => group.dataset.groupLabel || group.dataset.group);
        return {
          valid: false,
          message: `未選択：${labels.join('、')}`,
          issues: []
        };
      }

      const issues = checkCompatibility(getSelectedItems(), { includeMetadata: true });
      const errors = issues.filter((issue) => issue.type === 'error');

      if (errors.length > 0) {
        return {
          valid: false,
          message: '互換性エラーを解消してください。',
          issues
        };
      }

      return { valid: true, message: '', issues };
    }

    function getCandidateIssues(input) {
      const candidate = getItemFromInput(input);
      if (!candidate) return [];

      const items = getSelectedItems()
        .filter((item) => item.categoryKey !== candidate.categoryKey);
      items.push(candidate);

      return checkCompatibility(items, { includeMetadata: true })
        .filter((issue) =>
          issue.type === 'error' && issue.categories.includes(candidate.categoryKey)
        );
    }

    function updateOptionAvailability() {
      if (!compatibilityEnabled) return;

      partInputs.forEach((input) => {
        const card = input.closest('.pc-option-card');
        const group = input.closest('[data-option-group]');
        const reason = card?.querySelector('[data-compatibility-reason]');
        const stockDisabled = input.dataset.stockDisabled === 'true';
        const modelHidden = card?.hidden || group?.hidden || input.dataset.modelHidden === 'true';

        card?.classList.remove('pc-option-card--incompatible');
        if (modelHidden) {
          input.disabled = true;
          return;
        }

        if (reason) {
          reason.hidden = true;
          reason.textContent = '';
        }

        if (stockDisabled) {
          input.disabled = true;
          return;
        }

        if (input.checked) {
          input.disabled = false;
          const selectedIssues = checkCompatibility(getSelectedItems(), { includeMetadata: true })
            .filter((issue) => issue.type === 'error' && issue.categories.includes(input.dataset.categoryKey));

          if (selectedIssues.length > 0) {
            card?.classList.add('pc-option-card--incompatible');
            if (reason) {
              reason.hidden = false;
              reason.textContent = selectedIssues[0].message;
            }
          }
          return;
        }

        const candidateIssues = getCandidateIssues(input);
        input.disabled = candidateIssues.length > 0;

        if (candidateIssues.length > 0) {
          card?.classList.add('pc-option-card--incompatible');
          if (reason) {
            reason.hidden = false;
            reason.textContent = candidateIssues[0].message;
          }
        }
      });
    }

    function renderCompatibility(validation) {
      if (!compatibilityPanel || !compatibilityBadge || !compatibilityList) return;

      const missing = getMissingRequiredGroups();
      const issues = validation.issues || checkCompatibility(getSelectedItems(), { includeMetadata: true });
      const errors = issues.filter((issue) => issue.type === 'error');
      const warnings = issues.filter((issue) => issue.type === 'warning');

      compatibilityPanel.classList.remove(
        'pc-compatibility-status--pending',
        'pc-compatibility-status--ok',
        'pc-compatibility-status--warning',
        'pc-compatibility-status--error'
      );
      compatibilityList.innerHTML = '';

      let status = 'pending';
      let badge = '選択待ち';
      let messages = [];

      if (errors.length > 0) {
        status = 'error';
        badge = '問題あり';
        messages = errors.map((issue) => issue.message);
      } else if (warnings.length > 0) {
        status = 'warning';
        badge = '要確認';
        messages = warnings.map((issue) => issue.message);
      } else if (missing.length > 0) {
        status = 'pending';
        badge = '確認中';
        messages = ['残りの必須パーツを選択してください。'];
      } else {
        status = 'ok';
        badge = '問題なし';
        messages = ['登録された互換性データの範囲で問題は見つかりませんでした。'];
      }

      compatibilityPanel.classList.add(`pc-compatibility-status--${status}`);
      compatibilityBadge.textContent = badge;

      messages.slice(0, 8).forEach((message) => {
        const li = document.createElement('li');
        li.textContent = message;
        compatibilityList.appendChild(li);
      });
    }

    function renderSummary() {
      applyBtoModelConfiguration({ resetSelections: false });
      updateOptionAvailability();

      const items = getSelectedItems();
      const validation = validate();
      const total = items.reduce((sum, item) => sum + item.price, 0);

      summaryList.innerHTML = '';

      items.forEach((item) => {
        const li = document.createElement('li');
        const left = document.createElement('span');
        const category = document.createElement('span');
        const name = document.createElement('span');
        const price = document.createElement('span');

        category.className = 'pc-summary-list__category';
        category.textContent = item.category;
        name.className = 'pc-summary-list__name';
        name.textContent = item.label;
        price.className = 'pc-summary-list__price';
        price.textContent = formatMoney(item.price);

        left.append(category, name);
        li.append(left, price);
        summaryList.appendChild(li);
      });

      if (items.length === 0) {
        const li = document.createElement('li');
        li.textContent = '商品を選択してください。';
        summaryList.appendChild(li);
      }

      totalElement.textContent = formatMoney(total);
      addButton.disabled = !validation.valid || items.length === 0;
      errorElement.textContent = validation.message;
      renderCompatibility(validation);
    }

    function createBuildId() {
      const now = new Date();
      const pad = (value) => String(value).padStart(2, '0');
      const date = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}`;
      const time = `${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
      const random = Math.random().toString(36).slice(2, 6).toUpperCase();
      return `PASO-${date}-${time}-${random}`;
    }

    async function addToCart() {
      const validation = validate();
      if (!validation.valid) {
        errorElement.textContent = validation.message;
        renderCompatibility(validation);
        return;
      }

      const selected = getSelectedItems();
      if (selected.length === 0) return;

      addButton.disabled = true;
      addButton.textContent = 'カートに追加中…';
      errorElement.textContent = '';

      const buildId = createBuildId();
      const orderType = mode === 'bto' ? 'BTOカスタマイズ' : '完全オーダーメイド';
      const selectedBase = getItemFromInput(getSelectedBaseInput());
      const items = selected.map((item) => {
        const properties = {
          'PC構成ID': buildId,
          '注文方式': orderType,
          'パーツ区分': item.category,
          '選択内容': item.label,
          '互換性チェック': compatibilityEnabled ? '自動チェックOK' : '未実施'
        };

        if (mode === 'bto' && selectedBase) {
          properties['BTOベースモデル'] = selectedBase.label;
          properties['BTOモデルコード'] = selectedBase.modelCode;
        }

        return {
          id: item.variantId,
          quantity: 1,
          properties
        };
      });

      try {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({ items })
        });

        const result = await response.json();
        if (!response.ok) {
          throw new Error(result.description || 'カートに追加できませんでした。');
        }

        window.location.href = redirectAfterAdd === 'checkout'
          ? `${root}checkout`
          : `${root}cart`;
      } catch (error) {
        errorElement.textContent = error.message || 'エラーが発生しました。';
        addButton.disabled = false;
        addButton.textContent = 'この構成をカートに入れる';
      }
    }

    builder.addEventListener('change', (event) => {
      if (event.target.matches('input[type="radio"]')) {
        if (mode === 'bto' && event.target.dataset.categoryKey === 'base') {
          applyBtoModelConfiguration({ resetSelections: true });
        }
        renderSummary();
      }
    });

    addButton?.addEventListener('click', addToCart);

    applyBtoModelConfiguration({ resetSelections: true });
    renderSummary();
  });
})();
